import React, { useState, useEffect } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from './ui/button';

/**
 * UpdateBanner - Shows when a new version of the app is available
 * Gentle, non-intrusive notification aligned with MyLegacyBridge UX
 */
const UpdateBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [newVersion, setNewVersion] = useState('');

  useEffect(() => {
    // Listen for service worker update messages
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'SW_UPDATED') {
        setNewVersion(event.data.version || 'new');
        setShowBanner(true);
      }
    };

    // Check if service worker is supported and add listener
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }

    // Also listen for controllerchange (when new SW takes over)
    const handleControllerChange = () => {
      // New SW has taken control, suggest refresh
      setShowBanner(true);
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      }
    };
  }, []);

  const handleRefresh = () => {
    // Force reload to get new version
    window.location.reload(true);
  };

  const handleDismiss = () => {
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 animate-in slide-in-from-bottom-4"
      data-testid="update-banner"
    >
      <div className="bg-primary text-primary-foreground rounded-2xl shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary-foreground/10 rounded-xl shrink-0">
            <RefreshCw className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm mb-1">A new version is available</p>
            <p className="text-xs opacity-90">Refresh to get the latest features and improvements.</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="shrink-0 -mt-1 -mr-1 hover:bg-primary-foreground/10"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex gap-2 mt-3">
          <Button
            onClick={handleRefresh}
            className="flex-1 bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full"
            data-testid="refresh-app-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Now
          </Button>
          <Button
            variant="ghost"
            onClick={handleDismiss}
            className="hover:bg-primary-foreground/10 rounded-full"
          >
            Later
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpdateBanner;
